import { evaluateChatSlaBreach, evaluateTicketSlaBreach } from "@/lib/slaBreach";

/**
 * @param {string | null | undefined} iso
 * @returns {number | null}
 */
function parseIsoMs(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * Latest activity in the thread (messages with sentAt + SLA meta), for "newest on top" ordering.
 * @param {Record<string, unknown>} conv
 */
export function getChatLastActivityMs(conv) {
  let max = 0;
  const meta = conv.slaMeta && typeof conv.slaMeta === "object" ? conv.slaMeta : {};
  for (const key of ["lastUserAt", "lastHumanAgentAt", "firstHumanAgentAt", "firstUserAt"]) {
    const t = parseIsoMs(/** @type {string | undefined} */ (meta[key]));
    if (t != null) max = Math.max(max, t);
  }
  for (const m of conv.messages ?? []) {
    const t = parseIsoMs(m?.sentAt);
    if (t != null) max = Math.max(max, t);
  }
  return max > 0 ? max : Date.now();
}

/**
 * When the customer is waiting on a human agent, use the latest customer message time (FIFO urgency).
 * @param {Record<string, unknown>} conv
 */
function getCustomerWaitingSinceMs(conv) {
  const meta = conv.slaMeta && typeof conv.slaMeta === "object" ? conv.slaMeta : {};
  const lastUser = parseIsoMs(meta.lastUserAt);
  const lastAgent = parseIsoMs(meta.lastHumanAgentAt);
  if (lastUser == null) return null;
  const waiting = lastAgent == null || lastUser > lastAgent;
  return waiting ? lastUser : null;
}

/**
 * Inbox ordering similar to common helpdesk products:
 * 1. SLA-breached threads first (longest customer-wait first within that group).
 * 2. Otherwise newest activity first (fresh / "new" conversations rise to the top).
 *
 * @param {Array<Record<string, unknown>>} conversations
 * @param {{ chatFirstResponseSeconds: number, chatAverageResponseSeconds: number }} settings
 * @param {number} nowMs
 * @returns {Array<Record<string, unknown>>}
 */
export function sortChatsForInbox(conversations, settings, nowMs) {
  const list = Array.isArray(conversations) ? [...conversations] : [];
  return list.sort((a, b) => {
    const evA = evaluateChatSlaBreach(a.slaMeta ?? {}, settings, nowMs, a.status);
    const evB = evaluateChatSlaBreach(b.slaMeta ?? {}, settings, nowMs, b.status);
    if (evA.breached !== evB.breached) {
      return evA.breached ? -1 : 1;
    }
    if (evA.breached && evB.breached) {
      const wa = getCustomerWaitingSinceMs(a);
      const wb = getCustomerWaitingSinceMs(b);
      if (wa != null && wb != null && wa !== wb) {
        return wa - wb;
      }
      if (wa != null && wb == null) return -1;
      if (wa == null && wb != null) return 1;
      return getChatLastActivityMs(a) - getChatLastActivityMs(b);
    }
    const byActivity = getChatLastActivityMs(b) - getChatLastActivityMs(a);
    if (byActivity !== 0) return byActivity;
    const ua = typeof a.unread === "number" ? a.unread : 0;
    const ub = typeof b.unread === "number" ? b.unread : 0;
    return ub - ua;
  });
}

/**
 * @param {Record<string, unknown>} ticket
 */
function getTicketActivityMs(ticket) {
  const candidates = [
    ticket.lastRequesterActivityAt,
    ticket.lastAgentReplyAt,
    ticket.firstAgentReplyAt,
    ticket.statusChangedAt,
    ticket.createdAt,
  ];
  let max = 0;
  for (const iso of candidates) {
    const t = parseIsoMs(/** @type {string | undefined} */ (iso));
    if (t != null) max = Math.max(max, t);
  }
  return max > 0 ? max : Date.now();
}

function getTicketWaitingSinceMs(ticket) {
  const lastReq = parseIsoMs(/** @type {string | undefined} */ (ticket.lastRequesterActivityAt ?? ticket.createdAt));
  const lastAg = parseIsoMs(
    /** @type {string | undefined} */ (ticket.lastAgentReplyAt ?? ticket.firstAgentReplyAt)
  );
  if (lastReq == null) return null;
  const waiting = lastAg == null || lastReq > lastAg;
  return waiting ? lastReq : null;
}

/**
 * @param {Array<Record<string, unknown>>} tickets
 * @param {{ ticketFirstResponseSeconds: number, ticketAverageResponseSeconds: number }} settings
 * @param {number} nowMs
 */
export function sortTicketsForInbox(tickets, settings, nowMs) {
  const list = Array.isArray(tickets) ? [...tickets] : [];
  return list.sort((a, b) => {
    const evA = evaluateTicketSlaBreach(a, settings, nowMs);
    const evB = evaluateTicketSlaBreach(b, settings, nowMs);
    if (evA.breached !== evB.breached) {
      return evA.breached ? -1 : 1;
    }
    if (evA.breached && evB.breached) {
      const wa = getTicketWaitingSinceMs(a);
      const wb = getTicketWaitingSinceMs(b);
      if (wa != null && wb != null && wa !== wb) {
        return wa - wb;
      }
      if (wa != null && wb == null) return -1;
      if (wa == null && wb != null) return 1;
      return getTicketActivityMs(a) - getTicketActivityMs(b);
    }
    return getTicketActivityMs(b) - getTicketActivityMs(a);
  });
}
